import Phaser from 'phaser'
import { useGameStore } from '../stores/gameStore'
import { networkClient } from '../network/client'

const TILE_WIDTH = 64
const TILE_HEIGHT = 32
const MAP_WIDTH = 20
const MAP_HEIGHT = 15
const PLAYER_SPEED = 200

interface PlayerSprite extends Phaser.GameObjects.Sprite {
  playerId: string
  targetX: number
  targetY: number
}

export class GameScene extends Phaser.Scene {
  private player: PlayerSprite | null = null
  private otherPlayers: Map<string, PlayerSprite> = new Map()
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null
  private wasd: { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key } | null = null
  private lastSentPosition = { x: 0, y: 0 }
  private sendPositionTimer = 0

  constructor() {
    super({ key: 'GameScene' })
  }

  create() {
    // Create isometric floor
    this.createFloor()

    // Setup keyboard input
    this.cursors = this.input.keyboard?.createCursorKeys() ?? null
    this.wasd = {
      W: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      A: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      S: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      D: this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    }

    // Create player based on role
    this.createLocalPlayer()

    // Sync with other players
    this.setupNetworkSync()

    // Camera follow player
    if (this.player) {
      this.cameras.main.startFollow(this.player, true, 0.1, 0.1)
      this.cameras.main.setZoom(1.5)
    }
  }

  createFloor() {
    // Create a simple isometric grid
    const offsetX = this.cameras.main.width / 2
    const offsetY = 100

    for (let y = 0; y < MAP_HEIGHT; y++) {
      for (let x = 0; x < MAP_WIDTH; x++) {
        // Convert to isometric coordinates
        const isoX = (x - y) * (TILE_WIDTH / 2) + offsetX
        const isoY = (x + y) * (TILE_HEIGHT / 2) + offsetY

        // Add floor tile
        const tile = this.add.image(isoX, isoY, 'tile_floor')
        tile.setDepth(0)

        // Add wall tiles at edges
        if (x === 0 || y === 0 || x === MAP_WIDTH - 1 || y === MAP_HEIGHT - 1) {
          const wall = this.add.image(isoX, isoY, 'tile_wall')
          wall.setDepth(y + 1)
        }
      }
    }

    // Add some interactive markers (puzzle locations)
    const markers = [
      { x: 5, y: 5 },
      { x: 15, y: 5 },
      { x: 10, y: 10 },
    ]

    markers.forEach(({ x, y }) => {
      const isoX = (x - y) * (TILE_WIDTH / 2) + offsetX
      const isoY = (x + y) * (TILE_HEIGHT / 2) + offsetY

      const marker = this.add.image(isoX, isoY - 16, 'marker')
      marker.setDepth(1000)

      // Pulse animation
      this.tweens.add({
        targets: marker,
        scale: { from: 0.8, to: 1.2 },
        alpha: { from: 0.6, to: 1 },
        duration: 800,
        yoyo: true,
        repeat: -1,
      })
    })
  }

  createLocalPlayer() {
    const state = useGameStore.getState()
    const role = state.selectedRole

    const offsetX = this.cameras.main.width / 2
    const offsetY = 100

    // Start position in center of map
    const startX = 10
    const startY = 7
    const isoX = (startX - startY) * (TILE_WIDTH / 2) + offsetX
    const isoY = (startX + startY) * (TILE_HEIGHT / 2) + offsetY

    const spriteKey = `player_${role}`
    this.player = this.add.sprite(isoX, isoY - 24, spriteKey) as PlayerSprite
    this.player.playerId = state.mySessionId || 'local'
    this.player.targetX = isoX
    this.player.targetY = isoY - 24
    this.player.setDepth(1000)

    // Add name label
    this.addPlayerLabel(this.player, state.nickname || 'Gracz', role)
  }

  addPlayerLabel(sprite: PlayerSprite, name: string, role: string) {
    const colors: Record<string, string> = {
      flux: '#00FF66',
      logic: '#4488FF',
      spark: '#FF8844',
      echo: '#AA44FF',
    }

    const label = this.add.text(0, -40, name, {
      fontSize: '12px',
      color: colors[role] || '#ffffff',
      backgroundColor: '#000000aa',
      padding: { x: 4, y: 2 },
    })
    label.setOrigin(0.5, 0.5)

    // Attach label to sprite
    sprite.setData('label', label)
  }

  setupNetworkSync() {
    const room = networkClient.getGameRoom()
    if (!room) return

    const state = useGameStore.getState()
    const mySessionId = state.mySessionId

    // Listen for other players
    room.state.players.onAdd((player: any, sessionId: string) => {
      if (sessionId === mySessionId) return

      // Create sprite for other player
      const offsetX = this.cameras.main.width / 2
      const offsetY = 100
      const isoX = player.x || offsetX
      const isoY = player.y || offsetY + 200

      const spriteKey = `player_${player.role || 'flux'}`
      const sprite = this.add.sprite(isoX, isoY - 24, spriteKey) as PlayerSprite
      sprite.playerId = sessionId
      sprite.targetX = isoX
      sprite.targetY = isoY - 24
      sprite.setDepth(900)
      sprite.setAlpha(0.8)

      this.addPlayerLabel(sprite, player.nickname || 'Gracz', player.role || 'flux')
      this.otherPlayers.set(sessionId, sprite)

      // Track position changes
      player.onChange(() => {
        const s = this.otherPlayers.get(sessionId)
        if (s) {
          s.targetX = player.x
          s.targetY = player.y - 24
        }
      })
    })

    room.state.players.onRemove((_: any, sessionId: string) => {
      const sprite = this.otherPlayers.get(sessionId)
      if (sprite) {
        const label = sprite.getData('label') as Phaser.GameObjects.Text
        if (label) label.destroy()
        sprite.destroy()
        this.otherPlayers.delete(sessionId)
      }
    })
  }

  update(_time: number, delta: number) {
    if (!this.player || !this.cursors || !this.wasd) return

    // Handle input
    let velocityX = 0
    let velocityY = 0

    // Arrow keys
    if (this.cursors.left.isDown || this.wasd.A.isDown) {
      velocityX = -PLAYER_SPEED
    } else if (this.cursors.right.isDown || this.wasd.D.isDown) {
      velocityX = PLAYER_SPEED
    }

    if (this.cursors.up.isDown || this.wasd.W.isDown) {
      velocityY = -PLAYER_SPEED
    } else if (this.cursors.down.isDown || this.wasd.S.isDown) {
      velocityY = PLAYER_SPEED
    }

    // Apply movement
    if (velocityX !== 0 || velocityY !== 0) {
      // Normalize diagonal movement
      if (velocityX !== 0 && velocityY !== 0) {
        velocityX *= 0.707
        velocityY *= 0.707
      }

      this.player.x += velocityX * (delta / 1000)
      this.player.y += velocityY * (delta / 1000)
    }

    // Update player label position
    const label = this.player.getData('label') as Phaser.GameObjects.Text
    if (label) {
      label.x = this.player.x
      label.y = this.player.y - 40
    }

    // Update depth based on Y position (for proper overlapping)
    this.player.setDepth(Math.floor(this.player.y) + 1000)

    // Send position to server (throttled)
    this.sendPositionTimer += delta
    if (this.sendPositionTimer > 50) { // 20 times per second max
      this.sendPositionTimer = 0

      const dx = Math.abs(this.player.x - this.lastSentPosition.x)
      const dy = Math.abs(this.player.y - this.lastSentPosition.y)

      if (dx > 2 || dy > 2) {
        networkClient.sendMove(this.player.x, this.player.y)
        this.lastSentPosition.x = this.player.x
        this.lastSentPosition.y = this.player.y
      }
    }

    // Smoothly move other players to their target positions
    this.otherPlayers.forEach((sprite) => {
      const dx = sprite.targetX - sprite.x
      const dy = sprite.targetY - sprite.y

      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
        sprite.x += dx * 0.15
        sprite.y += dy * 0.15
      }

      // Update label
      const otherLabel = sprite.getData('label') as Phaser.GameObjects.Text
      if (otherLabel) {
        otherLabel.x = sprite.x
        otherLabel.y = sprite.y - 40
      }

      sprite.setDepth(Math.floor(sprite.y) + 900)
    })
  }
}
